// report.core.mixin.js
import { jsPDF } from 'jspdf';
import dayjs from 'dayjs';
import ReportTranslationMixin from '@shared_mixins/report/report.translation.mixin.js';
import ReportNamingMixin from '@shared_mixins/report/report.naming.mixin.js';
import {REPORT_RENDERERS} from '@shared_mixins/report/report-template';
const {installSafeText} = require('./report-text-safe.cjs');
const ReportCoverPNG = require('@shared/assets/img/common/report_cover_ge_plain.png');
const QESGLogoPNG = require('@shared/assets/img/common/QuantifiedESG_Logo_v3_Basic.png');


export default {
    mixins: [
        ReportTranslationMixin,
        ReportNamingMixin
    ],
    props: {
        report_lang: {
            required: true,
            type: String,
            default: 'ko'
        },
        pdf_filename: {
            required: true,
            type: String,
            default: ''
        },
        reportMode: {
            required: true,
            type: String,
            default: 'full'
        },
        needReport: {
            required: true,
            type: Boolean,
            default: false
        },
        assessmentType: {
            required: true,
            type: String,
            default: 'DOC'
        },
        reportData: {
            required: false,
            type: Object,
            default: () => ({ info: null, benchmarks: null, parser: null, risk: null, scoring: null })
        },
        is_reassessment: {
            required: false,
            type: Boolean,
            default: true
        },
        set_actual_duration: {
            required: false,
            type: Boolean,
            default: true
        },
        // 초안 결과보고서 라벨/안내문구 표시 여부 (custom prd 계열 포팅 — hyundai_v2 mixin 소비)
        is_draft: {
            required: false,
            type: Boolean,
            default: false
        },
        // 평가 날짜 커스텀: 체크 시 date-picker 범위로 평가기간을 override (hmc_theme mixin 소비)
        custom_duration: {
            required: false,
            type: Boolean,
            default: false
        },
        custom_start_date: {
            required: false,
            type: String,
            default: ''
        },
        custom_end_date: {
            required: false,
            type: String,
            default: ''
        }
    },
    data () {
        return {
            jsPDF_instance: null,
            local_pdf_filename: null,
            pageContentWidth: 0,
            pageContentHeight: 0,
            is_report_self_update: false,
            total_page_by_mode: 0,
            factors: null,

            // 상세평가 auditor prefix
            auditor_shortname_prefix: [
                '김수헌', '(유광)', 'chj', 'dy', 'fy', 'fy;', 'hu', 'js', 'sh', '권강미',
                '강민정(240729)', '강민정(240731)', '강민정(240604)', '강민정(240605)', '강민정(240607)', '강민정(240610)', '강민정(240612)', '강민정(240731)',
                '김동영', '김현서', '성정민', '우대위', '이승주', '이종미', '이효지진',
                '이효진', '허은채', '.허은채', '허은채(240614)', '허은채(240618)'
            ], 
        };
    },
    computed: {
        jsPDFOpt () {
            return {
                // jsPDF static config
                a4: {
                    width: 595,
                    height: 842
                },
                margin:{
                    top: 15,
                    right: 30,
                    bottom: 35,
                    left: 30
                },
                headerHeight: 20,
                footerHeight: 30,
                headerStyles: this._jsPDF_headerStyles,
                footerStyles: this._jsPDF_footerStyles,
                spacing: {
                    section_lg: 35,
                    section_md: 27.5,
                    section_sm: 25,
                    section_xs: 15,
                    title: 3,
                    title_sm: 6,
                    title_xs: 5
                },
                ...this._jsPDF_font_config,
                card: this._jsPDF_card_config
            };
        },
        _jsPDF_headerStyles () {
            return {
                fontSize: 8.5,
                fillColor: [0, 44, 95],
                textColor: [255, 255, 255]
            };
        },
        _jsPDF_footerStyles () {
            return {
                fontSize: 8.5,
                fillColor: [228, 220, 211], // [246, 243, 242],
                textColor: [255, 255, 255]
            };
        },
        _jsPDF_font_config () {
            return {
                fontFamily: 'NotoSansKR',
                fontFamily_ca: 'NotoSansHK',
                fontSize: this._jsPDF_font_config_fontSize,
                fontColor: {
                    cover_name: '#212121',
                    cover_company_name: '#212121',
                    // title: "#212121",
                    title: '#212121',
                    title_sm: '#212121',
                    title_xs: '#212121',
                    table_th: '#292828', // "#212121",
                    table_td: '#292828', // "#616161",
                    default: '#263238',
                    footnote: '#757575'
                },
                fontWeight: {
                    cover_name: 'bold',
                    cover_company_name: 'bold',
                    title: 'semibold',
                    title_sm: 'medium',
                    title_xs: 'medium',
                    table_th: 'medium',
                    table_td: 'normal',
                    footnote: 'normal',
                    default: 'normal'
                }
            };
        },
        _jsPDF_font_config_fontSize () {
            if (this.report_lang === 'en') {
                return {
                    cover_name_lg: 30,
                    cover_company_name_lg: 24,
                    cover_name: 25,
                    cover_company_name: 16,
                    title: 13.5, // 15,
                    title_sm: 12, // 13,
                    title_xs: 10.5, // 11,
                    table_th: 9.5, // 9,
                    table_td: 8.5, // 8,
                    footnote: 8, // 9,
                    default: 9, // 7,
                };
            } else {
                return {
                    cover_name_lg: 30,
                    cover_company_name_lg: 24,
                    cover_name: 25,
                    cover_company_name: 16,
                    title: 15,
                    title_sm: 13,
                    title_xs: 11,
                    table_th: 9,
                    table_td: 8,
                    footnote: 9,
                    default: 7
                };
            }
        },
        _jsPDF_card_config () {
            return {
                fillColor: [246, 243, 242], // '#F8F8F8',
                borderColor: '#e4dcd3', // '#E0E0E0',
                lineWidth: 0.5
            };
        },
        tableStyles () {
            return {
                global: {
                    valign: 'middle',
                    overflow: 'linebreak',
                    lineColor: [222, 226, 230],
                    lineWidth: 0.1,
                    minCellHeight: 16,
                },
                th: {
                    // fontSize: this.report_lang === "ko" ? 11 : 10,
                    fontSize: this.report_lang === 'ko' ? 11 : 10.5,
                    font: this.report_lang !== 'ca' ? 'NotoSansKR' : 'NotoSansHK',
                    fontStyle: 'medium',
                    fillColor: [246, 243, 242],
                    lineColor: [228, 220, 211],
                    textColor: '#292828', // "#424242"
                    valign: 'middle'
                },
                td: {
                    font: this.report_lang !== 'ca' ? 'NotoSansKR' : 'NotoSansHK',
                    // fontSize: this.report_lang === "ko" ? 9.5 : 9.0,
                    fontSize: this.report_lang === 'ko' ? 9.5 : 9,
                    fontStyle: 'normal',
                    fillColor: '#FFFFFF', // [256, 256, 256],
                    // textColor: "#424242", // "#616161",
                    valign: 'middle'
                }
            };
        },
        datatables () {
            return {
                global: {
                    ...this.tableStyles.global,
                    halign: 'center'
                },
                th: {
                    ...this.tableStyles.th,
                    fontSize: 9.5
                },
                td: {
                    ...this.tableStyles.td,
                    fontSize: 9.5
                }
            };
        },
        factorstables () {
            return {
                global: {
                    ...this.tableStyles.global
                },
                th: {
                    ...this.tableStyles.th,
                    halign: 'center',
                    fontSize: 10
                },
                td: {
                    ...this.tableStyles.td,
                    fontSize: 9
                }
            };
        },
        scoring () {
            return JSON.parse(JSON.stringify(this.reportData.scoring));
        },
        assessment_type () {
            return this.reportData.info.service_type === '일반' ? 'DOC' : 'OSIS';
        },
        firstElementSpacing () {
            return this.jsPDFOpt.headerHeight + (this.jsPDFOpt.margin.top + 25);
        },
        auditor_shortname_prefix_regexp () {
            return new RegExp(`(${this.auditor_shortname_prefix.map(word => word + '\\s*:?\\s*').join('|')})`, 'g');
        },
        report_cover_image () {
            return ReportCoverPNG;
        },
        qesg_logo_png () {
            return QESGLogoPNG;   
        }
    },
    watch: {},
    async created () {
        if (this.pdf_filename === this.report_filename) {
            this.local_pdf_filename = this.pdf_filename;
        } else {
            this.local_pdf_filename = this.report_filename;
        }
        await this.addFontVFS();
    },
    methods: {
        /**
         * 평가기간 커스텀 override — custom_duration 체크 + 양쪽 날짜 존재 시
         * assessmentInfo 의 requested_date/duedate_date 를 커스텀 값으로 치환 (전 리포트 공용).
         * 각 report mixin 의 assessmentInfo 말미에서 호출한다. (custom prd 계열 포팅)
         */
        formatDurationDate (date) {
            if (!date) return ' - ';
            return this.report_lang === 'ko'
                ? dayjs(date).format('YYYY년 MM월 DD일')
                : dayjs(date).format('MM/DD/YYYY');
        },
        applyCustomDuration (assessment_info) {
            if (this.custom_duration && this.custom_start_date && this.custom_end_date) {
                assessment_info.requested_date = this.formatDurationDate(this.custom_start_date);
                assessment_info.duedate_date = this.formatDurationDate(this.custom_end_date);
            }
            return assessment_info;
        },
        /**
         * font 관련 유틸리티 메서드
         */
        setFont (doc, type = null, font_family = null, font_weight = null) {
            let [ _font_family, _font_weight, _font_size, _text_color ] = [ null, null, null ];
            if (!font_family) {
                _font_family = this.report_lang === 'ca'
                    ? this.jsPDFOpt.fontFamily_ca
                    : this.jsPDFOpt.fontFamily;
            } else {
                _font_family = font_family;
            }
            
            if (type === 'title') {
                _font_weight = this.jsPDFOpt.fontWeight.title;
                _font_size = this.jsPDFOpt.fontSize.title;
                _text_color = this.jsPDFOpt.fontColor.title;
            } else if (type === 'title_sm') {
                _font_size = this.jsPDFOpt.fontSize.title_sm;
                _text_color = this.jsPDFOpt.fontColor.title_sm;
                _font_weight = this.jsPDFOpt.fontWeight.title_sm;
            } else if (type === 'title_xs') {
                _font_size = this.jsPDFOpt.fontSize.title_xs;
                _text_color = this.jsPDFOpt.fontColor.title_xs;
                _font_weight = this.jsPDFOpt.fontWeight.title_xs;
            } else if (type === 'table_th') {
                _font_weight = this.jsPDFOpt.fontWeight.table_th;
                _font_size = this.jsPDFOpt.fontSize.table_th;
                _text_color = this.jsPDFOpt.fontColor.table_th;
            } else if (type === 'table_td') {
                _font_weight = this.jsPDFOpt.fontWeight.table_td;
                _font_size = this.jsPDFOpt.fontSize.table_td;
                _text_color = this.jsPDFOpt.fontColor.table_td;
            } else if (type === 'footnote') {
                _font_weight = this.jsPDFOpt.fontWeight.footnote;
                _font_size = this.jsPDFOpt.fontSize.footnote;
                _text_color = this.jsPDFOpt.fontColor.footnote;
            } else if (type === 'cover_name') {
                _font_weight = this.jsPDFOpt.fontWeight.cover_name;
                _font_size = this.jsPDFOpt.fontSize.cover_name;
                _text_color = this.jsPDFOpt.fontColor.cover_name;
            }
            else if (type === 'cover_name_lg') {
                _font_weight = this.jsPDFOpt.fontWeight.cover_name;
                _font_size = this.jsPDFOpt.fontSize.cover_name_lg;
                _text_color = this.jsPDFOpt.fontColor.cover_name;
            }
            else if (type === 'cover_company_name') {
                _font_weight = this.jsPDFOpt.fontWeight.cover_company_name;
                _font_size = this.jsPDFOpt.fontSize.cover_company_name;
                _text_color = this.jsPDFOpt.fontColor.cover_company_name;
            }
            else if (type === 'cover_company_name_lg') {
                _font_weight = this.jsPDFOpt.fontWeight.cover_company_name;
                _font_size = this.jsPDFOpt.fontSize.cover_company_name_lg;
                _text_color = this.jsPDFOpt.fontColor.cover_company_name;
            }
            else {
                _font_weight = this.jsPDFOpt.fontWeight.default;
                _font_size = this.jsPDFOpt.fontSize.default;
                _text_color = this.jsPDFOpt.fontColor.default;
            }

            if (font_weight) _font_weight = font_weight;

            doc.setFont(_font_family, _font_weight);
            doc.setFontSize(_font_size);
            doc.setTextColor(_text_color);
        },
        async addFontVFS () {
            await this.$report.loadFont();
            const { NotoSansKR, NotoSansSC } = this.$report.cachedFont;

            jsPDF.API.setDefaultFont = function () {
                this.addFileToVFS('NotoSansKR-Bold.woff2', NotoSansKR['bold']);
                this.addFont('NotoSansKR-Bold.woff2', 'NotoSansKR', 'bold', 'Identity-H');

                this.addFileToVFS('NotoSansKR-SemiBold.woff2', NotoSansKR['semibold']);
                this.addFont('NotoSansKR-SemiBold.woff2', 'NotoSansKR', 'semibold', 'Identity-H');
            
                this.addFileToVFS('NotoSansKR-Medium.woff2', NotoSansKR['medium']);
                this.addFont('NotoSansKR-Medium.woff2', 'NotoSansKR', 'medium', 'Identity-H');

                this.addFileToVFS('NotoSansKR-Regular.woff2', NotoSansKR['normal']);
                this.addFont('NotoSansKR-Regular.woff2', 'NotoSansKR', 'normal', 'Identity-H');

                // 중국어
                this.addFileToVFS('NotoSansHK-Bold.woff2', NotoSansSC['bold']);
                this.addFont('NotoSansHK-Bold.woff2', 'NotoSansHK', 'bold', 'Identity-H');

                this.addFileToVFS('NotoSansHK-SemiBold.woff2', NotoSansSC['semibold']);
                this.addFont('NotoSansHK-SemiBold.woff2', 'NotoSansHK', 'semibold', 'Identity-H');

                this.addFileToVFS('NotoSansHK-Medium.woff2', NotoSansSC['medium']);
                this.addFont('NotoSansHK-Medium.woff2', 'NotoSansHK', 'medium', 'Identity-H');

                this.addFileToVFS('NotoSansHK-Regular.woff2', NotoSansSC['normal']);
                this.addFont('NotoSansHK-Regular.woff2', 'NotoSansHK', 'normal', 'Identity-H');

                return this;
            };
        },
        /**
         * table 관련 유틸리티 함수
         */
        jspdf_common_draw_cell (data, opt = {}) {
            const { cell, doc } = data;
            const _opt = { ...opt };
            
            if (typeof _opt?.noHeader === 'undefined') {
                _opt.noHeader = true;
            }

            if (typeof _opt?.header === 'undefined') {
                _opt.header = {};
            }

            if (typeof _opt?.header?.noLeftLine === 'undefined') {
                _opt.header.noLeftLine = false;
            }
            
            if (cell?.raw && cell?.raw.styles?.fontStyle !== 'medium') {
                if (this.isChineseOrHanzi(cell.raw.content) && this.report_lang !== 'ca') {
                    this.setFont(doc, cell.styles.fontStyle, this.jsPDFOpt.fontFamily_ca);
                } else if (!this.isChineseOrHanzi(cell.raw.content) && this.report_lang === 'ca') {
                    this.setFont(doc, cell.styles.fontStyle, this.jsPDFOpt.fontFamily);
                    doc.setFontSize(8);
                }

                if (this.report_lang === 'ca') {
                    if (!this.isChineseOrHanzi(cell.raw.content)) {
                        this.setFont(doc, cell.styles.fontStyle, this.jsPDFOpt.fontFamily);
                        doc.setFontSize(8);
                    } else {
                        cell.styles.font = this.jsPDFOpt.fontFamily;
                    }
                } else {
                    if (this.isChineseOrHanzi(cell.raw.content)) {
                        this.setFont(doc, cell.styles.fontStyle, this.jsPDFOpt.fontFamily_ca);
                        doc.setFontSize(8);
                    } else {
                        cell.styles.font = this.jsPDFOpt.fontFamily;
                    }
                }
            }

            if (cell) {
                // Cell bottom line border
                doc.setLineWidth(0.75);
                doc.setDrawColor(222, 226, 230);
                doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
                
                // Title Cell left line border
                if (cell?.raw && cell.raw.styles?.fontStyle === 'medium') {
                    if (!_opt.header.noLeftLine) {
                        doc.setLineWidth(0.5);
                        doc.setDrawColor(222, 226, 230);
                        doc.line(cell.x + 0.5, cell.y, cell.x + 0.5, cell.y + cell.height);
                        doc.fill([246, 246, 246]);
                    }
                }

                // 첫 번째 행 (data.row.index === 0)인 경우 border-top 설정;
                if ((!_opt?.noHeader && cell.section === 'head' && data.row.index === 0) || (_opt?.noHeader && data.row.index === 0 && data.cell.section === 'body')) {
                    const { cell, doc } = data;
                    // 기존 스타일을 복사하여 새로운 스타일 객체 생성
                    const cellStyles = { ...cell.styles };
                    cellStyles.lineWidth = 1.5;
                    cellStyles.lineColor = _opt.headLineColor ?? [95, 153, 215];

                    // 현재 셀의 위치 계산
                    const startX = cell.x;
                    const startY = cell.y;
                    const endX = startX + cell.width;

                    // 상단 테두리 그리기
                    doc.setLineWidth(cellStyles.lineWidth);
                    doc.setDrawColor(cellStyles.lineColor[0], cellStyles.lineColor[1], cellStyles.lineColor[2]);
                    doc.line(startX, startY - (cellStyles.lineWidth / 3), endX, startY - (cellStyles.lineWidth / 3));
                }
            }
        },
        jspdf_common_cell_font_convert (data) {
            const { cell, doc } = data;
            
            if (cell?.raw && cell?.raw.styles?.fontStyle !== 'medium') {
                if (this.isChineseOrHanzi(cell.raw.content) && this.report_lang !== 'ca') {
                    this.setFont(doc, cell.styles.fontStyle, this._jsPDF_font_config.fontFamily_ca);
                } else if (!this.isChineseOrHanzi(cell.raw.content) && this.report_lang === 'ca') {
                    this.setFont(doc, cell.styles.fontStyle, this._jsPDF_font_config.fontFamily);
                    doc.setFontSize(8);
                }

                if (this.report_lang === 'ca') {
                    if (!this.isChineseOrHanzi(cell.raw.content)) {
                        this.setFont(doc, cell.styles.fontStyle, this._jsPDF_font_config.fontFamily);
                        doc.setFontSize(8);
                    } else {
                        cell.styles.font = this._jsPDF_font_config.fontFamily;
                    }
                } else {
                    if (this.isChineseOrHanzi(cell.raw.content)) {
                        this.setFont(doc, cell.styles.fontStyle, this._jsPDF_font_config.fontFamily_ca);
                        doc.setFontSize(8);
                    } else {
                        cell.styles.font = this._jsPDF_font_config.fontFamily;
                    }
                }
            }
        },
        /**
         * Page 관련 유틸리티 메서드 
         */
        generateCover (doc) {
            try {
                // 페이지 너비,높이
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();

                const maxWidth = pageWidth - 20; // 페이지의 여백을 고려한 너비
                const maxHeight = pageHeight - 20; // 페이지의 여백을 고려한 높이

                // // 보고서 커버 이미지
                let newWidth = maxWidth * 0.9;
                let newHeight = maxHeight * 0.9;

                // 수평, 수직 중앙정렬
                const [_cover_img_position_x, _cover_img_position_y] = [(maxWidth - newWidth) / 2, (maxHeight - newHeight) / 2];
                doc.addImage(this.report_cover_image, 'png', _cover_img_position_x, _cover_img_position_y, newWidth, newHeight);

                // "재평가 결과보고서" 문구 추가
                if (this.is_reassessment) {
                    this.setFont(doc, 'title');
                    const _reassessment_report_title = doc.splitTextToSize(`[${this.$t('재평가 결과보고서', this.report_lang)}]`, this.pageContentWidth);
                    doc.text(_reassessment_report_title, this.pageContentWidth, _cover_img_position_y, { align: 'right' });
                }

                // 심화평가 문구 추가 ("재평가 결과보고서" 문구가 추가되면 심화평가 문구는 추가 안됨)
                if (!this.is_reassessment && ((this.assessmentType && this.assessmentType === 'OSIS') || (!this.assessmentType && this.reportData.info.service_type === '심화'))) {
                    this.setFont(doc, 'title');
                    const _osis_report_title = doc.splitTextToSize(`[${this.$t('실사평가 결과보고서', this.report_lang)}]`, this.pageContentWidth);
                    doc.text(_osis_report_title, this.pageContentWidth, _cover_img_position_y, { align: 'right' });
                }
                
                // 보고서 이름 추가
                this.setFont(doc, 'cover_name');
                const _report_title_split_width = this.reportData?.info?.campaign
                    ? [505, 504].includes(this.reportData?.info?.campaign.id)
                        ? maxWidth / 2
                        : maxWidth / 2.6
                    : this.IS_KIA
                        ? maxWidth
                        : maxWidth / 2;
  
                const _report_title = doc.splitTextToSize(this.$t(this.cover_title, this.report_lang), (_report_title_split_width));
                const [_report_title_width, _report_title_height] = [doc.getTextDimensions(_report_title).w, doc.getTextDimensions(_report_title).h];

                let _text_position_x = null;
                if (this.reportData?.info?.campaign) {
                    if ([505, 504].includes(this.reportData?.info?.campaign.id)) {
                        _text_position_x = this.report_lang === 'ca'
                            ? maxWidth - _report_title_width - 3
                            : this.report_lang === 'en'
                                ? maxWidth - _report_title_width - 23
                                : maxWidth - _report_title_width - 26;
                    } else if (this.IS_KIA) {
                        _text_position_x = this.report_lang === 'ca'
                            ? maxWidth - _report_title_width - 53.5
                            : this.report_lang === 'en'
                                ? maxWidth - _report_title_width - 45
                                : maxWidth - _report_title_width - 70;
                    } else {
                        _text_position_x = this.report_lang === 'ca'
                            ? maxWidth - _report_title_width - 75
                            : this.report_lang === 'en'
                                ? maxWidth - _report_title_width - 49
                                : maxWidth - _report_title_width - 46;
                    }
                }
                
                let _text_position_y = (maxHeight / 2) + (this.jsPDFOpt.margin.top * 1.25);
                doc.text(_report_title, _text_position_x, _text_position_y);

                // 보고서 기업명 추가
                if (this.report_lang === 'ca' && this.isChineseOrHanzi(this.companyInfo['회사명'])){
                    this.setFont(doc, 'cover_company_name', this.jsPDFOpt.fontFamily_ca);
                } else {
                    doc.setFont();
                    this.setFont(doc, 'cover_company_name', this.jsPDFOpt.fontFamily);
                }

                const _company_name = doc.splitTextToSize(this.companyInfo['회사명'], (maxWidth / 2));
                _text_position_y += _report_title_height + (this.jsPDFOpt.margin.top * 0.75);
                doc.text(_company_name, _text_position_x, _text_position_y);

                // QESG 로고 추가
                const logo_max_height = 20;
                const logo_QESG_width = (40 * logo_max_height) / 10;
                const logo_QESG_position_x = maxWidth - logo_QESG_width - 30;
                const logo_QESG_position_y = maxHeight - logo_max_height - 30;
                doc.addImage(this.qesg_logo_png, 'png', logo_QESG_position_x, logo_QESG_position_y, logo_QESG_width, logo_max_height);
            } catch (error) {
                console.log(error); 
            }
        },
        addHeader (doc) {
            doc.setFillColor(...this.jsPDFOpt.headerStyles.fillColor);
            
            this.setFont(doc, 'table_td', null, 'semibold');
            doc.setFontSize(this.jsPDFOpt.headerStyles.fontSize);
            doc.setTextColor(...this.jsPDFOpt.headerStyles.textColor);
            const [headerBox_position_x, headerBox_position_y] = [this.jsPDFOpt.margin.left, this.jsPDFOpt.margin.top + 10];
            doc.rect(headerBox_position_x, headerBox_position_y, this.pageContentWidth, this.jsPDFOpt.headerHeight, 'F');

            const report_header_padding = 10;
            let _report_serial_number = this.formatDate(new Date().toUTCString(), 'YYMMDD');
            _report_serial_number += `-${this.reportData.info.company_id}`;
            _report_serial_number += `-${this.reportData.info.id}`;
            
            _report_serial_number = doc.splitTextToSize(`${this.$t('발급번호', this.report_lang)} : ${_report_serial_number}`, this.pageContentWidth / 2);
            const _report_serial_number_dim = doc.getTextDimensions(_report_serial_number);
            const _report_serial_number_position_y = headerBox_position_y + ((this.jsPDFOpt.headerHeight - _report_serial_number_dim.h)) - 1;
            doc.text(_report_serial_number, headerBox_position_x + report_header_padding, _report_serial_number_position_y);

            const _report_company_header = doc.splitTextToSize(this.companyInfo['회사명'], doc.getTextWidth(this.companyInfo['회사명'] + 10));
            const _report_company_header_dim = doc.getTextDimensions(_report_company_header);
            const _report_company_header_position_x = this.jsPDFOpt.margin.left + this.pageContentWidth - doc.getTextWidth(this.companyInfo['회사명']) - report_header_padding;
            const _report_company_header_position_y = headerBox_position_y + ((this.jsPDFOpt.headerHeight - _report_company_header_dim.h)) - 1;
            
            if (this.report_lang === 'ca' && this.isChineseOrHanzi(this.companyInfo['회사명'])){
                this.setFont(doc, null, this.jsPDFOpt.fontFamily_ca);
            } else {
                this.setFont(doc, null, this.jsPDFOpt.fontFamily);
            }

            this.setFont(doc, 'table_td', null, 'semibold');
            doc.setFontSize(this.jsPDFOpt.headerStyles.fontSize);
            doc.setTextColor(...this.jsPDFOpt.headerStyles.textColor);
            doc.text(_report_company_header, _report_company_header_position_x, _report_company_header_position_y);
        },
        addFooter (doc) {
            // this.setFont(doc)
            this.setFont(doc, 'table_td', null, 'semibold');
            doc.setFontSize(this.jsPDFOpt.footerStyles.fontSize);
            doc.setFillColor(...this.jsPDFOpt.footerStyles.fillColor);
            
            const pageHeight = doc.internal.pageSize.getHeight();
            const [footerBox_position_x, footerBox_position_y] = [0, pageHeight - this.jsPDFOpt.footerHeight];
            const footerBox_width = doc.internal.pageSize.getWidth();
            doc.rect(footerBox_position_x, footerBox_position_y, footerBox_width, this.jsPDFOpt.footerHeight, 'F');

            // const _page_total = doc.internal.getNumberOfPages();
            const _page_total = doc.getNumberOfPages() - 1;
            const _page_current = doc.getCurrentPageInfo().pageNumber;
            
            // (좌측) QESG 로고
            const logo_max_height = 15;
            const logo_QESG_width = (40 * logo_max_height) / 10;
            const logo_QESG_position_y = pageHeight - (this.jsPDFOpt.footerHeight / 2) - 7.5;
            doc.addImage(this.qesg_logo_png, 'png', this.jsPDFOpt.margin.left, logo_QESG_position_y, logo_QESG_width, logo_max_height);
            
            // (우측) 페이지 넘버
            doc.setFontSize(10);
            const _footer_pages = doc.splitTextToSize(`${_page_current - 1} / ${_page_total}`, footerBox_width - this.jsPDFOpt.margin.right);
            const _footer_pages_height = doc.getTextDimensions(_footer_pages).h;
            const _footer_pages_width = doc.getTextDimensions(_footer_pages).w;
            const _footer_pages_position_x = footerBox_width - _footer_pages_width - (this.jsPDFOpt.margin.left);
            const _footer_pages_position_y = footerBox_position_y + (this.jsPDFOpt.footerHeight / 2) + (_footer_pages_height / 2);
            doc.text(_footer_pages, _footer_pages_position_x, _footer_pages_position_y);
        },
        addTitle (doc, position_y, level, title_name, opt = {}) {
            let _spacing = null;
            let _divider_width = null;

            if (!opt.skip_divider) {
                opt.skip_divider = false;
            }

            if (!opt.no_margin) {
                opt.no_margin = false;
            }

            if (!opt.no_padding) {
                opt.no_padding = false;
            }

            if (!opt.fixed_y) {
                opt.fixed_y = false;
            }

            let title_width = this.pageContentWidth;
            if (!opt.footnote) {
                opt.footnote = false;
            } else {
                let _footnote_font_weight = 'footnote';
                if (level === 1) {
                    _footnote_font_weight = 'title_sm';
                }

                this.setFont(doc, _footnote_font_weight);
                let _footnote_width = doc.getTextWidth(this.$t(opt.footnote, this.report_lang));
                let _footnote = doc.splitTextToSize(this.$t(opt.footnote, this.report_lang), _footnote_width);
                const _footnote_h = doc.getTextDimensions(_footnote).h;

                let _title = doc.splitTextToSize(this.$t(title_name, this.report_lang), title_width);
                const _title_h = doc.getTextDimensions(_title).h;
                doc.text(this.$t(opt.footnote, this.report_lang), (this.jsPDFOpt.margin.left + this.pageContentWidth) - _footnote_width, position_y + (_title_h - _footnote_h) + _footnote_h  + 2);

                title_width = (this.jsPDFOpt.margin.left + this.pageContentWidth) - _footnote_width;
            }

            let position_x = this.jsPDFOpt.margin.left;
            if (opt.position_x) {
                position_x = opt.position_x;
            }
            
            if (!opt.skip_divider) {
                _divider_width = !opt.divider_width
                    ? this.pageContentWidth + position_x
                    : opt.divider_width + position_x;
            }
            
            if (level === 1) {
                this.setFont(doc, 'title');
                let _title = doc.splitTextToSize(this.$t(title_name, this.report_lang), title_width);
                position_y += doc.getTextDimensions(_title).h;

                doc.text(_title, position_x, position_y);

                if (!opt.no_padding || !opt.fixed_y) {
                    position_y += 7;
                }

                if (!opt.skip_divider) {
                    doc.setLineWidth(1.5);
                    doc.setDrawColor('#343a40');
                    doc.line(position_x, position_y, _divider_width, position_y);   
                }
                
                if (!opt.no_margin) {
                    _spacing = 10;
                }
            } else if (level === 2) {
                this.setFont(doc, 'title_sm');
                let _title = doc.splitTextToSize(this.$t(title_name, this.report_lang), title_width);
                position_y += doc.getTextDimensions(_title).h;
                doc.text(_title, position_x, position_y);
                // position_y += 2

                if (!opt.no_padding || !opt.fixed_y) {
                    position_y += 5.5;
                }

                if (!opt.skip_divider) {
                    doc.setLineWidth(1);
                    doc.setDrawColor('#343a40');
                    doc.line(position_x, position_y, _divider_width, position_y);                    
                }
                
                if (!opt.no_margin) {
                    _spacing = 8.5;
                }
            } else if (level === 3) {
                this.setFont(doc, 'title_xs');
                let _title = doc.splitTextToSize(this.$t(title_name, this.report_lang), this.pageContentWidth);
                position_y += doc.getTextDimensions(_title).h;
                doc.text(_title, position_x, position_y);
                // position_y += 2

                if (!opt.no_padding && !opt.fixed_y) {
                    position_y += 4.5;
                }
                
                if (!opt.skip_divider) {
                    doc.setLineWidth(0.75);
                    doc.setDrawColor('#343a40');
                    doc.line(position_x, position_y, _divider_width, position_y);
                }

                if (!opt.no_margin) {
                    _spacing = 7.5;
                }

                if (opt.no_padding && opt.no_margin && opt.skip_divider) {
                    position_y += doc.getTextDimensions(_title).h + 1;
                }
            } else if (level === 4) {
                this.setFont(doc, 'title_xs', null, 'normal');
                let _title = doc.splitTextToSize(this.$t(title_name, this.report_lang), this.pageContentWidth);
                position_y += doc.getTextDimensions(_title).h;
                doc.text(_title, position_x, position_y);

                if (!opt.no_padding && !opt.fixed_y) {
                    position_y += 4.5;
                }
                
                if (!opt.skip_divider) {
                    doc.setLineWidth(0.75);
                    doc.setDrawColor('#343a40');
                    doc.line(position_x, position_y, _divider_width, position_y);
                }

                if (!opt.no_margin) {
                    _spacing = 6;
                }

                if (opt.no_padding && opt.no_margin && opt.skip_divider) {
                    position_y += doc.getTextDimensions(_title).h + 1;
                }
            }

            if (!opt.fixed_y) {
                position_y += _spacing;
            }
            
            return position_y;
        },
        checkNeedNewPage (doc, position_y, spacing = 50) {
            const _rest_space = this.pageContentHeight - position_y;
            let _is_new_page = false;
            if (_rest_space < spacing) {
                doc.addPage();
                this.addHeader(doc);

                _is_new_page = true;
            }

            return _is_new_page;
        },
        /**
         * ETC(기타) 유틸리티 메서드 
         */
        splitTextToFitWidth (text, width, doc) {
            const words = text.split(' ');
            let lines = [];
            let currentLine = '';

            for (let word of words) {
                let testLine = currentLine + word + ' ';
                let testWidth = doc.getTextWidth(testLine);

                if (testWidth > width && currentLine.length > 0) {
                    lines.push(currentLine.trim());
                    currentLine = word + ' ';
                } else {
                    currentLine = testLine;
                }
            }
            lines.push(currentLine.trim());
            return lines.filter(line => line !== '');
        },
        formatNumber(input) {
            if (input) {
                // 숫자 이외의 문자 제거
                let sanitizedInput = input.toString().replace(/[^\d.]/g, '');
                
                // 소수점이 두 개 이상 있는 경우 처리 (첫 번째 소수점 이후로는 모두 제거)
                const parts = sanitizedInput.split('.');
                if (parts.length > 2) {
                    sanitizedInput = parts[0] + '.' + parts.slice(1).join('');
                }

                // (필요한 경우) 숫자로 변환
                const number = parseFloat(sanitizedInput);

                if (isNaN(number)) {
                    return input;
                }

                // 천 단위로 콤마 넣기
                return number.toLocaleString('en-US', { maximumFractionDigits: 20 });
            } else {
                return input;
            }
        },
        isChineseOrHanzi (text) {
            const chineseHanziRegex = /[\u4E00-\u9FFF\u3400-\u4DBF\uF900-\uFAFF]/;
            // const chineseHanziRegex = /[\u4E00-\u9FFF\u3400-\u4DBF\uF900-\uFAFF0-9!-/:-@[-`{-~]/;
            // /[\u4e00-\u9fa5]/
            return chineseHanziRegex.test(text);
        },
        roundToOneDecimalPlace (value) {
            // const factor = Math.pow(10, decimalPlaces);
            // return Math.round(value * factor) / factor;
            return Number((Math.round(value * 10) / 10).toFixed(1));
        },
        removeAuditorShortNamePrefix(text) {
            // 접두사 리스트를 정의합니다.
            const prefixes = ['DY:', 'dy:', 'sy:', 'su:', 'hu:', 'jm:', 'jh:'];
            
            // 각 라인을 처리합니다.
            return text.split('\n').map(line => {
                // 각 라인에서 접두사가 있는지 확인하고 제거합니다.
                for (let prefix of prefixes) {
                    if (line.trim().startsWith(prefix) && !line.trim().startsWith('https') && !line.trim().startsWith('http')) {
                        return line.trim().slice(prefix.length).trim();
                    }
                }
                return line;
            }).join('\n'); //.toString();
        },
        removeAuditorNamePrefix(text) {
            // 접두사 리스트를 정의합니다.
            const prefixes = ['강현욱', 'test2', '유부원', '조혜진', '김동영', '이승주', '김상용', '손슬기', '김수헌', '강민정', '김현서', '허은채', '우대위', '이효진', '양유광', '권강미', '성정민', '이종미', '연원태', '이성수 위원', '심종섭 위원', '박중근 위원', '정재민', '신창식 팀장', '이창인', '송윤호', '최원호', '우상욱', '우상수 위원', '박만규', '박용익 위원', '임순홍 위원', '김은홍 위원', '이성수', '심종섭', '박중근', '신창식', '우상수', '박용익', '임순홍', '김은홍'];

            // 각 라인을 처리합니다.
            return text.split('\n').map(line => {
                // 각 라인에서 접두사가 있는지 확인하고 제거합니다.
                for (let prefix of prefixes) {
                    if (line.trim().startsWith(prefix) && !line.trim().startsWith('https') && !line.trim().startsWith('http')) {
                        return line.trim().slice(prefix.length).trim();
                    }
                }
                return line;
            }).join('\n').toString();
        },
        splitByManagement (strings) {
            if (Array.isArray(strings)) {
                return strings.map(str => {
                    let parts = null;
                    if (/(Management|management)/g.test(str)) {
                        // parts = [str.replace(/(Management|management)/g, '')];
                        parts = str.replace(/(Management|management)/g, '');
                    } else {
                        parts = str;
                    }
                    
                    // return parts.map(part => part.trim());
                    return parts.trim();
                });
            } else if (strings) {
                if (/(Management|management)/g.test(strings)) {
                    return strings.replace(/(Management|management)/g, '').trim();
                } else {
                    return strings.trim();
                }
            }
        },
        /**
         * 결과보고서 생성 관련 유틸리티 메서드
         */
        generatePDFfilename (_selected_assessment_type = 'DOC') {
            if (!_selected_assessment_type) {
                _selected_assessment_type = this.assessment_type;
            }

            let _title = this.report_filename;
            
            if (this.reportData.info.company.company_name) {
                _title += `_${this.reportData.info.company.company_name}`;
            }

            if (this.reportData.info.vendor_code) {
                _title += _title.length
                    ? `_${this.reportData.info.vendor_code}`
                    : this.reportData.info.vendor_code;
            }

            if (_selected_assessment_type) {
                const _assessment_type = this.assessment_type === 'DOC' ? this.$t('서면평가', this.report_lang) : this.$t('실사평가', this.report_lang);
                _title += _title.length ? `_${_assessment_type}` : _assessment_type;
            }

            _title += this.report_lang === 'ko' ? '_KOR' : this.report_lang === 'en' ? '_ENG' : '_CHI';
            return _title;
        },
        async generatePDF (need_blob_output = false) {
            this.initializeReport();
            this.local_pdf_filename = this.generatePDFfilename();
            let position_top = this.jsPDFOpt.headerHeight + this.jsPDFOpt.margin.top;
            let doc = new jsPDF({
                orientation: 'portrait',
                unit: 'px',
                format: 'a4'
            });

            // 커스텀 폰트 - 기본 폰트로 설정
            doc.setDefaultFont();
            // 탭 등 제어문자가 있으면 jsPDF 가 그 뒤 문장을 에러 없이 잘라버린다 — 그리기 전에 공백으로 바꾼다 (report-text-safe.cjs).
            installSafeText(doc);

            const _renderer = this.resolved_report_template ? REPORT_RENDERERS[this.resolved_report_template] : null;
            if (_renderer) {
                if (_renderer.appendCompanyToFilename) {
                    let _title = this.report_filename;

                    if (this.reportData.info.company.company_name) {
                        _title += `_${this.reportData.info.company.company_name}`;
                    }

                    this.local_pdf_filename = _title;
                }
                doc = await this[_renderer.generate](doc, position_top);
            }

            if (!need_blob_output) {
                return doc;
            } else {
                return doc.output('blob', {
                    filename: `${this.local_pdf_filename}.pdf`
                });
            }
        },
        initializeReport () {
            // HMC
            if (this.category1PieChartInstance) {
                this.category1PieChartInstance.destroy();
                this.category1PieChartInstance = null;
            }

            if (this.category1AreaChartInstance) {
                this.category1AreaChartInstance.destroy();
                this.category1AreaChartInstance = null;
            }        
    
            if (this.category1BarChartInstance) {
                this.category1BarChartInstance.destroy();
                this.category1BarChartInstance = null;
            }

            if (typeof this.PAR_BarChartInstance !== 'undefined' && this.PAR_BarChartInstance) {
                this.PAR_BarChartInstance.destroy();
                this.PAR_BarChartInstance = null;
            }

            if (typeof this.PAR_Category1AreAChartInstance !== 'undefined' && this.PAR_Category1AreAChartInstance) {
                this.PAR_Category1AreAChartInstance.destroy();
                this.PAR_Category1AreAChartInstance = null;
            }

            // KIA
            if (this.workerPieChartInstance) {
                this.workerPieChartInstance.destroy();
                this.workerPieChartInstance = null;
            }
    
            if (this.category1AreaChartInstance) {
                this.category1AreaChartInstance.destroy();
                this.category1AreaChartInstance = null;
            }
    
            if (this.category1ContraryAreaChartInstance) {
                this.category1ContraryAreaChartInstance.destroy();
                this.category1ContraryAreaChartInstance = null;
            }
    
            if (this.subsCompositionPieChartInstance) {
                this.subsCompositionPieChartInstance.destroy();
                this.subsCompositionPieChartInstance = null;
            }

            if (this.imporanceCompositionPieChartInstance) {
                this.imporanceCompositionPieChartInstance.destroy();
                this.imporanceCompositionPieChartInstance = null;
            }
            // hyundai 
            if (this.category2ContraryAreaChartInstance) {
                this.category2ContraryAreaChartInstance.destroy();
                this.category2ContraryAreaChartInstance = null;
            }
            
            // ksteel
            if (this.codeDoughnutChartInstance) {
                this.codeDoughnutChartInstance.destroy();
                this.codeDoughnutChartInstance = null;
            }
            
            // Common
            this.is_report_self_update = false;
            this.pageContentWidth = 0;
            this.pageContentHeight = 0;
            this.factors = null;

            // 링크 목적지는 ev_id 로 키잉돼 누적된다(예: report.supply.mixin.js:1305,:1319 이 넣고 :804 가 전부 순회).
            // 뷰어 하나로 여러 업체를 연속 생성하면 앞 업체의 목적지가 뒤 업체 PDF 에 남아 개선사항·부록 링크가 엉뚱한 곳으로 간다.
            // 키 구조가 렌더러마다 달라(supply/ksteel: toImprovement·toAppendix, hmc_2025: toAudit·toFactor) 기존 키를 그대로 비운다.
            if (this.linkDestinationInfo) {
                Object.keys(this.linkDestinationInfo).forEach(_key => {
                    this.linkDestinationInfo[_key] = {};
                });
            }

            this.jsPDF_instance = null;
        },
        getXFromScore (x_start, x_end, score) {
            return x_start + (score / 100) * (x_end - x_start);
        },
        drawScoreIndicator (doc, x, y, score, opt = {}) {
            if (typeof opt.label_position === 'undefined') {
                opt.label_position = 'top';
            }

            if (typeof opt.indicator_fill_color === 'undefined') {
                opt.indicator_fill_color = [0, 76, 153];
            }

            if (typeof opt.indicator_border_color === 'undefined') {
                opt.indicator_border_color = [0, 76, 153];
            }

            if (typeof opt.indicator_line_style === 'undefined') {
                opt.indicator_line_style = 'solid';
            }

            if (typeof opt.label_font_size === 'undefined') {
                opt.label_font_size = 9.5;
            }

            if (typeof opt.fill_background === 'undefined') {
                opt.fill_background = true;
            }

            if (typeof opt.size === 'undefined') {
                opt.size = 3;
            }

            const size = opt.size;
            const top = [x, y - size];
            const right = [x + size, y];
            const bottom = [x, y + size];
            const left = [x - size, y];
            const line_style_set = {
                solid: [],
                dotted: [1, 2],
                dashed: [3, 2]
            };
            
            doc.setLineWidth(1);
            doc.setDrawColor(...opt.indicator_border_color);  // 테두리 색상
            doc.setFillColor(...opt.indicator_fill_color);  // 원하는 색상
            if (opt.indicator_line_style === 'dashed') {
                doc.setLineDashPattern(line_style_set['dashed'], 0);
            } else if (opt.indicator_line_style === 'dotted') {
                doc.setLineDashPattern(line_style_set['dotted'], 0);
            } else {
                doc.setLineDashPattern(line_style_set['solid'], 0);
            }

            doc.line(...top, ...right);    // top → right
            doc.line(...right, ...bottom); // right → bottom
            doc.line(...bottom, ...left);  // bottom → left
            doc.line(...left, ...top);     // left → top

            doc.setLineWidth(0);
            doc.setLineDashPattern(line_style_set['solid'], 0);
            doc.setDrawColor(...opt.indicator_border_color);  // 테두리 색상
            doc.lines(
                [
                    [right[0] - top[0], right[1] - top[1]],
                    [bottom[0] - right[0], bottom[1] - right[1]],
                    [left[0] - bottom[0], left[1] - bottom[1]],
                    [top[0] - left[0], top[1] - left[1]]  // 마지막 꼭짓점 → 처음으로 닫음
                ],
                top[0],
                top[1],
                [1, 1],
                opt.fill_background ? 'F' : 'D'
            );

            const scoreStr = doc.splitTextToSize(score);
            const textDims = doc.getTextDimensions(scoreStr, { fontSize: opt.label_font_size });
            const textX = x - textDims.w / 2;
            let textY = 0;
            
            if (opt.label_position) {
                if (opt.label_position === 'bottom') {
                    textY = y + size + 10;
                } else if (opt.label_position === 'top') (
                    textY = y - size - 2
                );
            }
          
            doc.setFontSize(opt.label_font_size);
            doc.setTextColor(0);
            doc.text(scoreStr, textX, textY);
        },
        sortBySubCode(data) {
            return data.slice().sort((a, b) => {
                const extractParts = (code) => {
                    return code
                        .replace(/[a-zA-Z]/gi, '')
                        .split('.')
                        .map(Number);
                };                
            
                const [a1, a2] = extractParts(a.sub_code);
                const [b1, b2] = extractParts(b.sub_code);
            
                if (a1 !== b1) return a1 - b1;
                return a2 - b2;
            });
        }
    }
};
